<template>
  <div class="top-products-table">
    <div v-if="!products || products.length === 0" class="empty-state">
      <i class="fas fa-box-open"></i>
      <p>No product data available</p>
    </div>

    <div v-else class="table-container">
      <table class="products-table">
        <thead>
          <tr>
            <th class="rank-col">#</th>
            <th>Product</th>
            <th class="text-right">Qty Sold</th>
            <th class="text-right">Revenue</th>
            <th class="text-right">% of Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(product, index) in displayProducts" :key="product.product_id" class="product-row">
            <td class="rank-col">
              <div class="rank-badge" :class="getRankClass(index)">
                <i v-if="index < 3" :class="getTrophyIcon(index)"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
            </td>

            <td>
              <div class="product-info">
                <a class="product-name" :href="productLink(product)">{{ product.name }}</a>
                <div class="product-meta">
                  <span class="profit-badge">
                    +KES {{ formatNumber(product.profit) }} profit
                  </span>
                </div>
              </div>
            </td>

            <td class="text-right">
              <a class="qty-value" :href="productLink(product)">{{ formatNumber(product.quantity_sold, 0) }}</a>
            </td>

            <td class="text-right">
              <span class="revenue-value">KES {{ formatNumber(product.revenue) }}</span>
            </td>

            <td class="text-right">
              <div class="percent-cell">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: product.percent_of_total + '%' }"></div>
                </div>
                <span class="percent-text">{{ product.percent_of_total }}%</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="products.length > limit" class="table-footer">
        <button @click="showAll = !showAll" class="btn-show-more">
          {{ showAll ? 'Show Top 10' : `Show All ${products.length} Products` }}
          <i :class="showAll ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TopProductsTable',

  props: {
    products: {
      type: Array,
      required: true
    },
    limit: {
      type: Number,
      default: 10
    },
    startDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      showAll: false
    };
  },

  computed: {
    displayProducts() {
      return this.showAll ? this.products : this.products.slice(0, this.limit);
    }
  },

  methods: {
    productLink(product) {
      return `/inventory-activity?only-sales=1&p-identifier-id=${product.product_id}&start-date=${this.startDate}&end-date=${this.endDate}`;
    },

    formatNumber(value, decimals = 2) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    },

    getRankClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return '';
    },

    getTrophyIcon(index) {
      if (index === 0) return 'fas fa-trophy';
      if (index === 1) return 'fas fa-medal';
      if (index === 2) return 'fas fa-award';
      return '';
    }
  }
};
</script>

<style scoped>
.top-products-table {
  height: 100%;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.table-container {
  overflow-x: auto;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
}

.products-table thead {
  border-bottom: 2px solid #e2e8f0;
}

.products-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.products-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.products-table tbody tr:hover {
  background: #f8fafc;
}

.products-table td {
  padding: 14px 16px;
}

.rank-col {
  width: 60px;
  text-align: center !important;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
  background: #f1f5f9;
  color: #64748b;
}

.rank-gold {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
}

.rank-silver {
  background: linear-gradient(135deg, #e5e7eb 0%, #9ca3af 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.3);
}

.rank-bronze {
  background: linear-gradient(135deg, #fb923c 0%, #ea580c 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(251, 146, 60, 0.3);
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.product-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  text-decoration: none;
}

.product-name:hover,
.qty-value:hover {
  color: #1e293b;
  text-decoration: underline;
}

.product-meta {
  display: flex;
  gap: 8px;
}

.profit-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #dcfce7;
  color: #16a34a;
  font-weight: 600;
}

.text-right {
  text-align: right;
}

.qty-value {
  font-weight: 600;
  color: #475569;
  font-size: 14px;
  text-decoration: none;
}

.revenue-value {
  font-weight: 700;
  color: #1e293b;
  font-size: 14px;
}

.percent-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.progress-bar {
  width: 80px;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.percent-text {
  font-size: 12px;
  font-weight: 700;
  color: #3b82f6;
}

.table-footer {
  padding: 16px;
  text-align: center;
  border-top: 1px solid #e2e8f0;
}

.btn-show-more {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-show-more:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
</style>
